import type { Validations, Violation, WorkbenchSaveMetadata } from "@credit-acceptance/step6-bff-client";

import { getProblemDetail, isHttpError } from "../../../../../lib/query/errors";
import { stripFieldPrefix, type TranslatedViolations, translateViolations } from "../../../serverViolations";
import type { GapInformationInput } from "./GapInformation.schema";
import type { VehicleServiceContractInformationInput } from "./VehicleServiceContractInformation.schema";

/** `metadata.warnings[].section` set when the BFF saved the product but could not write the selected form to CAS (207). */
const ANCILLARY_FORM_WARNING_SECTION = "ancillary-form";

type VscPath = keyof VehicleServiceContractInformationInput;
type GapPath = keyof GapInformationInput;

/** BFF VSC violation field -> this form's field. */
const VSC_FIELD_MAP: Record<string, VscPath> = {
  vendorId: "tpapVendor",
  vendorName: "tpapVendor",
  certificateNo: "certificateNumber",
  sellingPrice: "sellingPrice",
  termMonths: "termMonths",
  termMiles: "termMiles",
  unlimitedMonths: "termMonths",
  unlimitedMileage: "termMiles",
  effectiveDate: "effectiveDate",
  formNo: "formNumber",
};

/** BFF GAP violation field -> this form's field. */
const GAP_FIELD_MAP: Record<string, GapPath> = {
  vendorId: "tpapVendor",
  vendorName: "tpapVendor",
  addendumNo: "addendumNumber",
  sellingPrice: "sellingPrice",
  term: "termCoverage",
  amountFinanced: "amountFinanced",
  effectiveDate: "effectiveDate",
  formNo: "formNumber",
};

const VSC_TPAP_ONLY = new Set<VscPath>(["termMonths", "termMiles", "formNumber"]);
const GAP_TPAP_ONLY = new Set<GapPath>(["termCoverage", "amountFinanced", "effectiveDate", "formNumber"]);

/** The non-TPAP VSC layout shows both terms in one read-only Term field. */
const VSC_NON_TPAP_FALLBACK: Partial<Record<VscPath, VscPath>> = {
  termMonths: "termDisplay",
  termMiles: "termDisplay",
};

interface ProductPathRules<TPath extends string> {
  /** `vsc` or `gap` — the only product prefix this card accepts. */
  readonly product: "vsc" | "gap";
  readonly fieldMap: Record<string, TPath>;
  /** Fields only the TPAP layout renders. */
  readonly tpapOnly: ReadonlySet<TPath>;
  /** Where a TPAP-only field's finding goes on the non-TPAP layout, if anywhere. */
  readonly nonTpapFallback?: Partial<Record<TPath, TPath>>;
}

/**
 * Resolves a product violation's field to a rendered input. Accepts a bare field, `<product>.<field>` and
 * `additionalProducts.<product>.<field>`; a field scoped to another product, or nested deeper, stays unmapped.
 * @template TPath
 * @param {string | undefined} field - The violation's `field`.
 * @param {ProductPathRules<TPath>} rules - The card's mapping rules.
 * @param {boolean} tpap - Whether the TPAP layout is rendered.
 * @returns {TPath | undefined} The form field, if rendered.
 */
function resolveProductPath<TPath extends string>(
  field: string | undefined,
  rules: ProductPathRules<TPath>,
  tpap: boolean
): TPath | undefined {
  if (!field) {
    return undefined;
  }
  const relative = stripFieldPrefix(stripFieldPrefix(field, ["additionalProducts"]), [rules.product]);
  const path = relative.includes(".") ? undefined : rules.fieldMap[relative];
  if (!path || tpap || !rules.tpapOnly.has(path)) {
    return path;
  }
  return rules.nonTpapFallback?.[path];
}

const VSC_RULES: ProductPathRules<VscPath> = {
  product: "vsc",
  fieldMap: VSC_FIELD_MAP,
  tpapOnly: VSC_TPAP_ONLY,
  nonTpapFallback: VSC_NON_TPAP_FALLBACK,
};

const GAP_RULES: ProductPathRules<GapPath> = { product: "gap", fieldMap: GAP_FIELD_MAP, tpapOnly: GAP_TPAP_ONLY };

/**
 * Translates `additionalProducts.vsc.validations` (or a failed save's `vsc.*` violations) onto the VSC card.
 * @param validations The VSC validations.
 * @param context Rendering context.
 * @param context.tpap Whether the TPAP layout (terms and form number) is rendered.
 * @returns {TranslatedViolations<VscPath>} Field errors plus unmapped findings.
 */
export function translateVscViolations(
  validations: Validations | undefined,
  context: { tpap: boolean }
): TranslatedViolations<VscPath> {
  return translateViolations(validations, (violation) => resolveProductPath(violation.field, VSC_RULES, context.tpap));
}

/**
 * Translates `additionalProducts.gap.validations` (or a failed save's `gap.*` violations) onto the GAP card.
 * @param validations The GAP validations.
 * @param context Rendering context.
 * @param context.tpap Whether the TPAP layout (term, amount financed, effective date, form number) is rendered.
 * @returns {TranslatedViolations<GapPath>} Field errors plus unmapped findings.
 */
export function translateGapViolations(
  validations: Validations | undefined,
  context: { tpap: boolean }
): TranslatedViolations<GapPath> {
  return translateViolations(validations, (violation) => resolveProductPath(violation.field, GAP_RULES, context.tpap));
}

/**
 * The 422 violations a failed save carries for one product (a bad vendor/form pair is rejected before anything is
 * written). Violations name their product as `vsc.<field>` / `gap.<field>`.
 * @param error The save error.
 * @param product `vsc` or `gap`.
 * @returns {Violation[]} That product's violations, empty for any other failure.
 */
export function getSaveViolations(error: unknown, product: "vsc" | "gap"): Violation[] {
  if (!isHttpError(error, 422)) {
    return [];
  }
  return getProblemDetail(error)?.violations?.filter((violation) => violation.field?.startsWith(`${product}.`)) ?? [];
}

/**
 * Adds a failed save's violations to the product's dashboard validations so they render in the same place.
 * @param validations The product's `validations`.
 * @param saveViolations Violations from `getSaveViolations`.
 * @returns {Validations | undefined} The combined validations.
 */
export function withSaveViolations(
  validations: Validations | undefined,
  saveViolations: Violation[]
): Validations | undefined {
  return saveViolations.length === 0
    ? validations
    : { ...validations, businessRuleViolations: [...(validations?.businessRuleViolations ?? []), ...saveViolations] };
}

/**
 * @param metadata A save response's metadata.
 * @returns {string | undefined} The 207 `ancillary-form` warning's message, when the form write failed.
 */
export function getAncillaryFormWarning(metadata: WorkbenchSaveMetadata | undefined): string | undefined {
  return metadata?.warnings?.find((warning) => warning.section === ANCILLARY_FORM_WARNING_SECTION)?.message;
}
